import React, { ReactNode, forwardRef } from 'react';
import { Trigger } from 'src/services/useInvoiceDetailsApi';
import { isFirefoxBrowser } from 'src/utils';

enum ButtonSizePaddingHorizontalMap {
  'sm' = 'h-[25px] px-2 text-[10px]',
  'md' = 'h-[30px] px-3 text-[13px]',
  'lg' = 'h-[35px] px-4 text-sm',
}

export const PreCheckResult = ['Allowed', 'NotValidTrigger', 'NoPermission', 'Blocked'] as const;
export const ButtonStateValues = ['Enabled', 'Disabled', 'Hidden'] as const;
export type ButtonState = 0 | 1 | 2;

enum ButtonColorMap {
  'primary' = 'bg-primary hover:bg-primary-hover',
  'secondary' = 'bg-secondary hover:bg-secondary-hover',
  'info' = 'bg-info hover:bg-sky-500/50',
  'error' = 'bg-red-500  hover:bg-error',
  'warning' = 'bg-warning text-dark',
  'success' = 'bg-primary',
  'dark' = 'bg-dark',
  'light' = 'bg-light border border-gray-500 text-dark',
  'transparent' = 'bg-transparent text-light hover:bg-light hover:text-dark',
  'disabled' = 'bg-disabled',
  'submit' = 'bg-submit hover:bg-submit-hover text-dark',
}

export type ButtonColor = keyof typeof ButtonColorMap;

export interface ButtonProps {
  size?: 'sm' | 'lg' | 'md';
  children: ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: () => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  buttonState?: ButtonState;
  readonly?: boolean;
  id?: string;
  color?: ButtonColor;
}

const fetchButtonStateByPreCheckResult = (result: keyof typeof PreCheckResult) => {
  if (result === 0) {
    return 0;
  } else if (result === 1 || result === 2) {
    return 2;
  } else {
    return 1;
  }
};

export const isButtonEnabled = (state: ButtonState) => state === 0;
export const isButtonDisabled = (state: ButtonState) => state === 1;
export const isButtonHidden = (state: ButtonState) => state === 2;

export const validateButtonState = (state: ButtonState, hasError: boolean): ButtonState =>
  hasError && state === 0 ? 1 : state;

export const getButtonState = (
  triggers: Trigger[],
  trigger: string,
  triggerEnum: string[]
): [string[], ButtonState] => {
  const triggerObject = triggers?.filter(
    (triggerObject) => triggerObject.trigger === triggerEnum.indexOf(trigger)
  )?.[0];

  if (!triggerObject) return [[], 2];
  return [triggerObject.messages, fetchButtonStateByPreCheckResult(triggerObject.preCheckResult)];
};

const Button = forwardRef<HTMLButtonElement | null, ButtonProps>(
  (
    {
      size = 'lg',
      onClick,
      children,
      startIcon,
      endIcon,
      disabled = false,
      loading = false,
      color = 'primary',
      className,
      onKeyDown,
      buttonState,
      readonly = false,
      id,
    },
    ref
  ) => {
    if (buttonState === 2) {
      return <></>;
    }

    disabled = disabled || buttonState === 1;

    return (
      <button
        className={`m-1 ${
          ButtonSizePaddingHorizontalMap[size]
        } focus:outline focus:outline-2 focus:outline-black whitespace-nowrap text-white ${
          !disabled && ButtonColorMap[color]
        } ${
          (disabled || loading) &&
          'bg-disabled cursor-not-allowed text-disabledtext hover:bg-disabled'
        }
         ${readonly && 'pointer-events-none'}
         ${className || ''} ${isFirefoxBrowser ? (size === 'lg' ? 'pb-[2px]' : '') : ''} ${
          loading ? '!relative' : ''
        }`}
        onClick={!disabled && !loading ? onClick : undefined}
        ref={ref}
        tabIndex={!disabled || !loading ? 0 : -1}
        type="submit"
        id={id}
        onKeyDown={onKeyDown}
        disabled={disabled || loading}
      >
        {!!loading && (
          <span className="k-icon k-i-loading text-lg !absolute left-[50%] -translate-x-[50%] mt-[3px]" />
        )}
        <span
          className={`flex items-center gap-2 justify-center w-full ${loading ? 'invisible' : ''}`}
        >
          {startIcon}
          {children}
          {endIcon}
        </span>
      </button>
    );
  }
);

export default Button;
